import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AnimatedSection from '../../components/base/AnimatedSection';
import SEOHead from '../../components/base/SEOHead';

const QuickStartPage = () => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'concepts' | 'guide'>('overview');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://openclaw.cc';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: 'OpenClaw 快速开始',
    description: '三步快速上手 OpenClaw AI 智能体网关 - 安装、配置、启动 Gateway 网关服务',
    url: `${siteUrl}/quick-start`,
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    author: {
      '@type': 'Organization',
      name: 'OpenClaw',
    },
    publisher: {
      '@type': 'Organization',
      name: 'OpenClaw',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/lobster.svg`,
      },
    },
    inLanguage: 'zh-CN',
  };

  const copyToClipboard = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const features = [
    {
      icon: 'ri-chat-3-line',
      title: '多渠道 Gateway 网关',
      desc: '通过单个 Gateway 网关进程连接 WhatsApp、Telegram、Discord 和 iMessage。'
    },
    {
      icon: 'ri-plug-line',
      title: '插件渠道',
      desc: '通过扩展包添加 Mattermost 等更多渠道。'
    },
    {
      icon: 'ri-route-line',
      title: '多智能体路由',
      desc: '按智能体、工作区或发送者隔离会话。'
    },
    {
      icon: 'ri-image-line',
      title: '媒体支持',
      desc: '发送和接收图片、音频和文档。'
    },
    {
      icon: 'ri-dashboard-line',
      title: 'Web 控制界面',
      desc: '浏览器仪表板，用于聊天、配置、会话和节点管理。'
    },
    {
      icon: 'ri-smartphone-line',
      title: '移动节点',
      desc: '配对 iOS 和 Android 节点，支持 Canvas。'
    }
  ];

  const steps = [
    {
      num: 1,
      title: '安装 OpenClaw',
      desc: '通过 npm 全局安装最新版本',
      code: 'npm install -g openclaw@latest',
      lang: 'shell'
    },
    {
      num: 2,
      title: '新手引导并安装服务',
      desc: '运行引导向导，完成初始化配置并安装后台服务',
      code: 'openclaw onboard --install-daemon',
      lang: 'shell'
    },
    {
      num: 3,
      title: '配对 WhatsApp 并启动 Gateway 网关',
      desc: '登录消息渠道，然后启动 Gateway 网关服务',
      code: 'openclaw channels login\nopenclaw gateway --port 18789',
      lang: 'shell'
    }
  ];

  const configExample = `{
  channels: {
    whatsapp: {
      allowFrom: ["+15555550123"],
      groups: { "*": { requireMention: true } },
    },
  },
  messages: { groupChat: { mentionPatterns: ["@openclaw"] } },
}`;

  const nextSteps = [
    {
      icon: 'ri-file-list-3-line',
      title: '文档中心',
      desc: '所有文档和指南，按用例分类。',
      link: '/help'
    },
    {
      icon: 'ri-settings-4-line',
      title: '配置',
      desc: '核心 Gateway 网关设置、令牌和提供商配置。',
      link: '/gateway'
    },
    {
      icon: 'ri-remote-control-line',
      title: '远程访问',
      desc: 'SSH 和 tailnet 访问模式。',
      link: '/gateway'
    },
    {
      icon: 'ri-message-2-line',
      title: '渠道',
      desc: 'WhatsApp、Telegram、Discord 等渠道的具体设置。',
      link: '/channels'
    },
    {
      icon: 'ri-node-tree',
      title: '节点',
      desc: 'iOS 和 Android 节点的配对与 Canvas 功能。',
      link: '/agents'
    },
    {
      icon: 'ri-question-answer-line',
      title: '帮助',
      desc: '常见修复方法和故障排除入口。',
      link: '/help'
    }
  ];

  const learnMore = [
    {
      icon: 'ri-list-check-2',
      title: '完整功能列表',
      desc: '全部渠道、路由和媒体功能。',
      link: '/channels'
    },
    {
      icon: 'ri-git-branch-line',
      title: '多智能体路由',
      desc: '工作区隔离和按智能体的会话管理。',
      link: '/agents'
    },
    {
      icon: 'ri-shield-check-line',
      title: '安全',
      desc: '令牌、白名单和安全控制。',
      link: '/gateway'
    },
    {
      icon: 'ri-bug-line',
      title: '故障排除',
      desc: 'Gateway 网关诊断和常见错误。',
      link: '/help'
    },
    {
      icon: 'ri-award-line',
      title: '关于与致谢',
      desc: '项目起源、贡献者和许可证。',
      link: '/about'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="OpenClaw 快速开始 | 三步上手 AI 智能体网关"
        description="三步快速上手 OpenClaw AI 智能体网关 - 安装、配置、启动 Gateway 网关服务，支持 WhatsApp、Telegram、Discord 等多渠道接入"
        keywords="OpenClaw快速开始, AI智能体网关, Gateway网关, WhatsApp接入, Telegram接入"
        canonical={`${siteUrl}/quick-start`}
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-orange-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-red-500 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-10 relative z-10">
          <AnimatedSection>
            <div className="flex flex-col items-center text-center">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-orange-500/15 border border-orange-500/30 rounded-full mb-6">
                <i className="ri-rocket-line text-orange-400 text-sm"></i>
                <span className="text-sm text-orange-400 font-medium">快速开始</span>
              </div>
              <h1 className="text-5xl font-bold text-white mb-5 leading-tight">
                OpenClaw 🦞
              </h1>
              <p className="text-lg text-slate-300 max-w-2xl mb-3 leading-relaxed">
                <em>"去壳！去壳！"</em> — 大概是一只太空龙虾说的
              </p>
              <p className="text-base text-slate-400 max-w-2xl mb-8 leading-relaxed">
                适用于任何操作系统的 AI 智能体 Gateway 网关，支持 WhatsApp、Telegram、Discord、iMessage 等。<br />
                发送消息，随时随地获取智能体响应。通过插件可添加 Mattermost 等更多渠道。
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <a
                  href="https://docs.openclaw.ai/zh-CN/start/getting-started"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-7 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-400 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-book-open-line mr-2"></i>入门指南
                </a>
                <a
                  href="https://docs.openclaw.ai/zh-CN/start/wizard"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-7 py-3 bg-white/10 text-white border border-white/20 rounded-lg font-semibold hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-magic-line mr-2"></i>运行向导
                </a>
                <a
                  href="https://github.com/openclaw/openclaw"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-7 py-3 bg-white/10 text-white border border-white/20 rounded-lg font-semibold hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-github-fill mr-2"></i>GitHub
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Tab Nav */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-10">
          <div className="flex gap-8">
            {[
              { key: 'overview', label: '概览', icon: 'ri-home-4-line' },
              { key: 'concepts', label: '核心功能', icon: 'ri-cpu-line' },
              { key: 'guide', label: '快速开始', icon: 'ri-terminal-line' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`flex items-center gap-2 py-4 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-orange-500 text-orange-500'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* What is OpenClaw */}
          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-10">
              <AnimatedSection>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <div>
                    <h2 className="text-3xl font-bold text-slate-900 mb-5">什么是 OpenClaw？</h2>
                    <p className="text-slate-600 leading-relaxed mb-5">
                      OpenClaw 通过单个 Gateway 网关进程将聊天应用连接到 Pi 等编程智能体。它为 OpenClaw 助手提供支持，并支持本地或远程部署。
                    </p>
                    <p className="text-slate-600 leading-relaxed mb-6">
                      Gateway 网关是会话、路由和渠道连接的唯一事实来源。无论你使用 WhatsApp 还是 Telegram，所有消息都通过同一个网关统一处理。
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <span className="px-3 py-1.5 bg-orange-50 text-orange-600 rounded-full text-sm font-medium border border-orange-200">开源免费</span>
                      <span className="px-3 py-1.5 bg-slate-50 text-slate-600 rounded-full text-sm font-medium border border-slate-200">跨平台部署</span>
                      <span className="px-3 py-1.5 bg-slate-50 text-slate-600 rounded-full text-sm font-medium border border-slate-200">多渠道支持</span>
                      <span className="px-3 py-1.5 bg-slate-50 text-slate-600 rounded-full text-sm font-medium border border-slate-200">本地 & 远程</span>
                    </div>
                  </div>
                  <div className="bg-slate-900 rounded-2xl p-8 border border-slate-700">
                    <div className="text-center mb-6">
                      <span className="text-slate-400 text-sm font-medium uppercase tracking-wider">工作原理</span>
                    </div>
                    <div className="space-y-3">
                      {[
                        { from: 'WhatsApp / Telegram / Discord', arrow: true },
                        { from: 'OpenClaw Gateway 网关', arrow: true, highlight: true },
                        { from: 'Pi 智能体 / 自定义 Agent', arrow: false }
                      ].map((item, i) => (
                        <div key={i} className="flex flex-col items-center">
                          <div className={`w-full px-4 py-3 rounded-lg text-sm font-medium text-center ${
                            item.highlight
                              ? 'bg-orange-500 text-white'
                              : 'bg-slate-800 text-slate-300 border border-slate-700'
                          }`}>
                            {item.from}
                          </div>
                          {item.arrow && (
                            <div className="w-px h-5 bg-slate-600 my-1 flex items-center justify-center">
                              <i className="ri-arrow-down-line text-slate-500 text-xs"></i>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-slate-500 text-xs text-center mt-5">Gateway 网关是会话、路由和渠道连接的唯一事实来源</p>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </section>

          {/* Dashboard Preview */}
          <section className="py-16 bg-slate-50">
            <div className="max-w-7xl mx-auto px-10">
              <AnimatedSection>
                <div className="text-center mb-10">
                  <h2 className="text-3xl font-bold text-slate-900 mb-4">仪表板</h2>
                  <p className="text-slate-600">Gateway 网关启动后，打开浏览器控制界面</p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                  <div className="space-y-5">
                    <div className="flex items-start gap-4 bg-white rounded-xl p-5 border border-slate-200">
                      <div className="w-10 h-10 flex items-center justify-center bg-orange-50 rounded-lg shrink-0">
                        <i className="ri-computer-line text-orange-500 text-xl"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">本地默认地址</h3>
                        <code className="text-sm text-orange-500 bg-orange-50 px-2 py-0.5 rounded">http://127.0.0.1:18789/</code>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 bg-white rounded-xl p-5 border border-slate-200">
                      <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-lg shrink-0">
                        <i className="ri-global-line text-slate-600 text-xl"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">远程访问</h3>
                        <p className="text-sm text-slate-500">支持 Web 界面和 Tailscale 远程访问模式</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 bg-white rounded-xl p-5 border border-slate-200">
                      <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-lg shrink-0">
                        <i className="ri-smartphone-line text-slate-600 text-xl"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">移动节点</h3>
                        <p className="text-sm text-slate-500">配对 iOS 和 Android 节点，支持 Canvas 功能</p>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
                    <img
                      src="https://mintcdn.com/clawdhub/FaXdIfo7gPK_jSWb/whatsapp-openclaw.jpg?fit=max&auto=format&n=FaXdIfo7gPK_jSWb&q=85&s=b74a3630b0e971f466eff15fbdc642cb"
                      alt="OpenClaw 仪表板示例"
                      className="w-full object-cover object-top"
                    />
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </section>
        </>
      )}

      {/* Core Features Tab */}
      {activeTab === 'concepts' && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-10">
            <AnimatedSection>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-slate-900 mb-4">核心功能</h2>
                <p className="text-slate-600 max-w-xl mx-auto">OpenClaw 提供完整的 AI 智能体网关能力，覆盖消息渠道、路由管理、媒体处理等核心场景</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
                {features.map((f, i) => (
                  <div key={i} className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-orange-300 hover:shadow-md transition-all">
                    <div className="w-11 h-11 flex items-center justify-center bg-orange-50 rounded-lg mb-4">
                      <i className={`${f.icon} text-xl text-orange-500`}></i>
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2">{f.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                  </div>
                ))}
              </div>

              {/* Learn More Cards */}
              <div className="border-t border-slate-100 pt-12">
                <h3 className="text-xl font-bold text-slate-900 mb-6">了解更多</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {learnMore.map((item, i) => (
                    <Link
                      key={i}
                      to={item.link}
                      className="flex items-start gap-4 bg-white rounded-xl p-5 border border-slate-200 hover:border-orange-300 hover:shadow-md transition-all cursor-pointer group"
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-lg shrink-0 group-hover:bg-orange-50 transition-colors">
                        <i className={`${item.icon} text-slate-500 group-hover:text-orange-500 transition-colors`}></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-1 text-sm">{item.title}</h4>
                        <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* Quick Start Guide Tab */}
      {activeTab === 'guide' && (
        <>
          <section className="py-16 bg-white">
            <div className="max-w-5xl mx-auto px-10">
              <AnimatedSection>
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-slate-900 mb-4">三步快速上手</h2>
                  <p className="text-slate-600">按照以下步骤，几分钟内启动你的 OpenClaw Gateway 网关</p>
                </div>

                {/* Steps */}
                <div className="relative">
                  <div className="absolute left-6 top-8 bottom-8 w-px bg-slate-200 hidden md:block"></div>
                  <div className="space-y-8">
                    {steps.map((step, i) => (
                      <div key={i} className="flex gap-6 items-start">
                        <div className="w-12 h-12 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold text-lg shrink-0 z-10 shadow-md">
                          {step.num}
                        </div>
                        <div className="flex-1 bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden">
                          <div className="px-6 py-4 border-b border-slate-200">
                            <h3 className="font-semibold text-slate-900 text-lg">{step.title}</h3>
                            <p className="text-sm text-slate-500 mt-1">{step.desc}</p>
                          </div>
                          <div className="bg-slate-900 relative">
                            <div className="flex items-center justify-between px-5 py-2 bg-slate-800 border-b border-slate-700">
                              <div className="flex items-center gap-1.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
                                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                                <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
                              </div>
                              <button
                                onClick={() => copyToClipboard(step.code, i)}
                                className="flex items-center gap-1.5 px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded text-xs transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <i className={copiedIndex === i ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                                {copiedIndex === i ? '已复制' : '复制'}
                              </button>
                            </div>
                            <pre className="px-6 py-5 text-sm text-slate-200 overflow-x-auto leading-relaxed">
                              <code>{step.code}</code>
                            </pre>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Config Example */}
                <div className="mt-12 bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-900">配置（可选）</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      配置文件位于 <code className="bg-slate-200 px-1.5 py-0.5 rounded text-slate-700 text-xs">~/.openclaw/openclaw.json</code>
                    </p>
                  </div>
                  <div className="px-6 py-4 bg-slate-50 text-sm text-slate-600 space-y-2">
                    <p>• 如果你<strong>不做任何修改</strong>，OpenClaw 将使用内置的 Pi 二进制文件以 RPC 模式运行，并按发送者创建独立会话。</p>
                    <p>• 如果你想要限制访问，可以从 <code className="bg-slate-200 px-1.5 py-0.5 rounded text-slate-700 text-xs">channels.whatsapp.allowFrom</code> 和（针对群组的）提及规则开始配置。</p>
                  </div>
                  <div className="bg-slate-900 relative">
                    <div className="flex items-center justify-between px-5 py-2 bg-slate-800 border-b border-slate-700">
                      <span className="text-xs text-slate-400">~/.openclaw/openclaw.json</span>
                      <button
                        onClick={() => copyToClipboard(configExample, 99)}
                        className="flex items-center gap-1.5 px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded text-xs transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className={copiedIndex === 99 ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                        {copiedIndex === 99 ? '已复制' : '复制'}
                      </button>
                    </div>
                    <pre className="px-6 py-5 text-sm text-slate-200 overflow-x-auto leading-relaxed">
                      <code>{configExample}</code>
                    </pre>
                  </div>
                </div>

                {/* Dashboard Info */}
                <div className="mt-10 bg-orange-50 border border-orange-200 rounded-2xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-lg shrink-0">
                      <i className="ri-information-line text-orange-500 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">启动后访问仪表板</h4>
                      <p className="text-sm text-slate-600 mb-3">Gateway 网关启动后，打开浏览器控制界面：</p>
                      <div className="flex flex-wrap gap-3">
                        <a
                          href="http://127.0.0.1:18789/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-orange-200 rounded-lg text-sm text-orange-600 hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-external-link-line"></i>
                          http://127.0.0.1:18789/
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </section>

          {/* Next Steps */}
          <section className="py-16 bg-slate-50">
            <div className="max-w-7xl mx-auto px-10">
              <AnimatedSection>
                <h2 className="text-2xl font-bold text-slate-900 mb-8">从这里开始</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {nextSteps.map((item, i) => (
                    <Link
                      key={i}
                      to={item.link}
                      className="flex items-start gap-4 bg-white rounded-xl p-5 border border-slate-200 hover:border-orange-300 hover:shadow-md transition-all cursor-pointer group"
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-lg shrink-0 group-hover:bg-orange-50 transition-colors">
                        <i className={`${item.icon} text-slate-500 group-hover:text-orange-500 transition-colors`}></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-1 text-sm">{item.title}</h4>
                        <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </AnimatedSection>
            </div>
          </section>
        </>
      )}

      <Footer />
    </div>
  );
};

export default QuickStartPage;
